import { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2, XCircle, Smartphone, Mail, Clock, Trophy, X } from 'lucide-react';
import { submitVote, getVoteStatus } from '../api/voting';
import { MpesaLogo, PaypalLogo } from './Logos';

const PACKAGES = [
  { votes: 20,   name: 'Spark',    color: '#0dcaf0', icon: 'fa-solid fa-bolt-lightning' },
  { votes: 50,   name: 'Rocket',   color: '#0d6efd', icon: 'fa-solid fa-rocket'         },
  { votes: 100,  name: 'Bazooka',  color: '#dc3545', icon: 'fa-solid fa-bomb'           },
  { votes: 200,  name: 'Firework', color: '#fd7e14', icon: 'fa-solid fa-burst'          },
  { votes: 500,  name: 'Crown',    color: '#ffc107', icon: 'fa-solid fa-crown'          },
  { votes: 1000, name: 'Jumbo',    color: '#6f42c1', icon: 'fa-solid fa-hippo'          },
];

const SAFARICOM_RANGES = [
  [700, 729], [740, 748], [757, 759],
  [768, 769], [790, 799], [110, 115],
];

export function normalizeMpesa(input) {
  if (!input) return '';
  let digits = String(input).replace(/\D/g, '');
  digits = digits.replace(/^0+/, '');
  if (!digits.startsWith('254')) digits = '254' + digits;
  return digits;
}

export function isValidSafaricom(normalized) {
  if (!/^\d{12}$/.test(normalized)) return false;
  const prefix = parseInt(normalized.slice(3, 6), 10);
  return SAFARICOM_RANGES.some(([lo, hi]) => prefix >= lo && prefix <= hi);
}

const POLL_TIMEOUT = 120;

export default function PaymentModal({ open, onClose, nominee, votes, survey }) {
  const [tab, setTab]                   = useState('mpesa');
  const [phone, setPhone]               = useState('');
  const [email, setEmail]               = useState('');
  const [phoneErr, setPhoneErr]         = useState('');
  const [emailErr, setEmailErr]         = useState('');
  const [state, setState]               = useState('idle');
  const [errorMsg, setErrorMsg]         = useState('');
  const [ref, setRef]                   = useState(null);
  const [secondsLeft, setSecondsLeft]   = useState(POLL_TIMEOUT);
  const [selectedVotes, setSelectedVotes] = useState(votes);

  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const charges      = Number(survey?.charges) || 0;
  const exchangeRate = Number(survey?.exchange_rate) || 0;
  const totalKes     = selectedVotes * charges;
  const usdAmount    = +(totalKes * exchangeRate).toFixed(2);
  const minVotes     = charges > 0 && exchangeRate > 0 ? Math.ceil(1 / (charges * exchangeRate)) : null;
  const paypalBlocked = tab === 'paypal' && (exchangeRate <= 0 || usdAmount < 1);

  const nonJumbo = PACKAGES.filter(p => p.votes !== 1000 && p.votes >= votes);
  const eligiblePackages = nonJumbo.slice(0, 3);
  if (votes <= 1000) eligiblePackages.push(PACKAGES.find(p => p.votes === 1000));

  useEffect(() => {
    if (open) {
      setTab('mpesa');
      setPhone(''); setEmail('');
      setPhoneErr(''); setEmailErr('');
      setState('idle'); setErrorMsg('');
      setRef(null); setSecondsLeft(POLL_TIMEOUT);
      setSelectedVotes(votes);
      document.body.classList.add('modal-open-scroll-lock');
    } else {
      document.body.classList.remove('modal-open-scroll-lock');
    }
    return () => {
      stopPolling();
      document.body.classList.remove('modal-open-scroll-lock');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopPolling = () => {
    clearInterval(pollRef.current);
    clearInterval(tickRef.current);
    pollRef.current = null;
    tickRef.current = null;
  };

  const startPolling = (paymentRef) => {
    stopPolling();
    setSecondsLeft(POLL_TIMEOUT);
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          stopPolling();
          setState('error');
          setErrorMsg(
            tab === 'paypal'
              ? 'Your payment was initiated. Please complete it in the PayPal window. You will be notified as soon as the payment is confirmed.'
              : 'Payment not confirmed. Please try again.'
          );
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    pollRef.current = setInterval(async () => {
      try {
        const res = await getVoteStatus(paymentRef);
        if (res?.data?.paid === true) {
          stopPolling();
          setState('success');
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const handleSubmit = async () => {
    setErrorMsg(''); setPhoneErr(''); setEmailErr('');
    const payload = { nominee_code: nominee.votingCode, votes: Number(selectedVotes), payment_gateway: tab };

    if (tab === 'mpesa') {
      const normalized = normalizeMpesa(phone);
      if (!isValidSafaricom(normalized)) {
        setPhoneErr('Enter a valid Safaricom number (e.g. 0712435678)');
        return;
      }
      payload.phone = normalized;
    } else {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setEmailErr('Enter a valid email address');
        return;
      }
      if (paypalBlocked) {
        setEmailErr(`Minimum ${minVotes ?? '?'} votes required for PayPal ($1.00 minimum). Current total: $${usdAmount}`);
        return;
      }
      payload.email = email.trim();
    }

    setState('submitting');
    try {
      const res = await submitVote(payload);
      if (res?.status === 'success' && res?.data?.ref) {
        setRef(res.data.ref);
        if (res.checkout === 'paypal' && res.data.paypal_url) {
          window.open(res.data.paypal_url, '_blank', 'noopener,noreferrer');
        }
        setState('polling');
        startPolling(res.data.ref);
      } else {
        setState('error');
        setErrorMsg(res?.message || 'Could not initiate payment.');
      }
    } catch (e) {
      setState('error');
      setErrorMsg(e?.response?.data?.message || e?.message || 'Network error.');
    }
  };

  const handleClose = () => {
    if (state === 'polling' || state === 'submitting') return;
    stopPolling();
    onClose();
  };

  const retry = () => { setState('idle'); setErrorMsg(''); setSecondsLeft(POLL_TIMEOUT); };

  const progressPct = ((POLL_TIMEOUT - secondsLeft) / POLL_TIMEOUT) * 100;
  const canClose = state !== 'polling' && state !== 'submitting';

  if (!open) return null;

  return (
    <div
      className="payment-modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className="payment-modal card border-0 shadow-lg">

        {/* Hero — ink surface header */}
        <div className="ink-surface p-4 position-relative">
          {canClose && (
            <button type="button" onClick={handleClose} className="btn btn-sm position-absolute top-0 end-0 m-2 text-white opacity-75" aria-label="Close">
              <X size={18} />
            </button>
          )}
          <div className="d-flex align-items-center gap-3">
            <div className="rounded-circle gold-chip d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 42, height: 42 }}>
              <Trophy size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 className="font-display h5 mb-0 text-white">Cast your votes for</h2>
              <p className="mb-0 small text-white-50 text-truncate">{nominee?.name} • {nominee?.categoryName}</p>
            </div>
          </div>
          <div className="row g-2 mt-3">
            <Stat label="Votes" value={selectedVotes} wide />
            {tab === 'mpesa'
              ? <Stat label="Total KES" value={`KSh ${totalKes.toLocaleString()}`} wide />
              : <Stat label="Total USD"  value={exchangeRate > 0 ? `$${usdAmount}` : '—'} wide />
            }
          </div>

          {eligiblePackages.length > 0 && state === 'idle' && (
            <div className="mt-3">
              <p className="pkg-label">⚡ Boost your vote — pick a package</p>
            <div className="pkg-row mt-2">
              {eligiblePackages.map((pkg) => (
                <button
                  key={pkg.votes}
                  type="button"
                  onClick={() => setSelectedVotes(pkg.votes)}
                  className={`pkg-chip ${selectedVotes === pkg.votes ? 'pkg-chip--active' : ''}`}
                >
                  <i className={`${pkg.icon} pkg-chip__icon`} />
                  <span className="pkg-chip__name">{pkg.name}</span>
                  <span className="pkg-chip__votes">{pkg.votes} votes</span>
                </button>
              ))}
            </div>
            </div>
          )}
        </div>

        <div className="p-4">

          {/* ── idle: payment form ── */}
          {state === 'idle' && (
            <>
              <ul className="nav nav-pills nav-fill bg-light rounded-pill p-1 mb-3" role="tablist">
                <li className="nav-item">
                  <button type="button" className={`nav-link rounded-pill d-inline-flex align-items-center justify-content-center gap-2 ${tab === 'mpesa' ? 'active' : 'text-body'}`} onClick={() => setTab('mpesa')}>
                    <Smartphone size={16} /> M-Pesa
                  </button>
                </li>
                <li className="nav-item">
                  <button type="button" className={`nav-link rounded-pill d-inline-flex align-items-center justify-content-center gap-2 ${tab === 'paypal' ? 'active' : 'text-body'}`} onClick={() => setTab('paypal')}>
                    <Mail size={16} /> PayPal
                  </button>
                </li>
              </ul>

              {tab === 'mpesa' && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="form-label fw-semibold mb-0">Safaricom Voting Number</label>
                    <MpesaLogo height={24} />
                  </div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    placeholder="0712435678"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setPhoneErr(''); }}
                    className={`form-control form-control-lg font-mono ${phoneErr ? 'is-invalid' : ''}`}
                    autoFocus
                  />
                  {phoneErr && <div className="invalid-feedback d-block">{phoneErr}</div>}
                  <p className="form-text mb-0 mt-1">You'll receive an STK push on your phone.</p>
                </div>
              )}

              {tab === 'paypal' && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="form-label fw-semibold mb-0">PayPal email</label>
                    <PaypalLogo height={24} />
                  </div>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailErr(''); }}
                    className={`form-control form-control-lg ${emailErr ? 'is-invalid' : ''}`}
                    autoFocus
                  />
                  {emailErr && <div className="invalid-feedback d-block">{emailErr}</div>}
                  <p className="form-text mb-0 mt-1">
                    {exchangeRate > 0 ? (
                      <>
                        USD equivalent: <span className="fw-semibold">${usdAmount}</span>
                        {paypalBlocked && minVotes && (
                          <> — minimum <span className="fw-semibold">{minVotes} votes</span> required ($1.00 minimum).</>
                        )}
                      </>
                    ) : 'USD pricing unavailable.'}
                  </p>
                </div>
              )}

              <div className="d-flex gap-2 mt-4">
                <button className="btn btn-outline-secondary flex-grow-1" onClick={handleClose}>Cancel</button>
                <button className="btn gold-chip flex-grow-1 fw-semibold" disabled={paypalBlocked} onClick={handleSubmit}>
                  Pay {tab === 'mpesa' ? `KSh ${totalKes.toLocaleString()}` : `$${usdAmount}`}
                </button>
              </div>

              <div className="d-flex align-items-center justify-content-center gap-3 mt-3 opacity-75">
                <small className="text-muted">Secure voting via</small>
                <MpesaLogo height={18} />
                <PaypalLogo height={18} />
              </div>
            </>
          )}

          {/* ── submitting ── */}
          {state === 'submitting' && (
            <div className="text-center py-4">
              <Loader2 size={40} className="text-warning mb-3 spin" />
              <p className="font-display fs-5 mb-0">Sending payment request…</p>
              <p className="text-muted small">One moment</p>
            </div>
          )}

          {/* ── polling ── */}
          {state === 'polling' && (
            <div className="text-center py-2">
              <div className="mx-auto rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                   style={{ width: 56, height: 56, background: 'hsl(38 88% 52% / 0.15)' }}>
                <Loader2 size={28} className="text-warning spin" />
              </div>
              <h3 className="font-display fs-5 mb-1">Awaiting confirmation</h3>
              <p className="text-muted small mb-3">
                {tab === 'mpesa'
                  ? 'Check your phone for the M-Pesa prompt and enter your PIN.'
                  : 'Complete the payment in the PayPal window.'}
              </p>
              <div className="progress" style={{ height: 8 }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${progressPct}%`, background: 'linear-gradient(135deg, hsl(38 90% 56%), hsl(28 92% 48%))', transition: 'width 1s linear' }}
                  aria-valuenow={progressPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <div className="d-flex align-items-center justify-content-center gap-1 mt-2 small text-muted font-mono">
                <Clock size={14} />
                {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:{String(secondsLeft % 60).padStart(2, '0')} remaining
              </div>
              <div className="d-flex justify-content-center mt-3">
                {tab === 'mpesa' ? <MpesaLogo height={22} /> : <PaypalLogo height={22} />}
              </div>
              {ref && <p className="small text-muted mt-2 mb-0">Ref: <span className="font-mono">{ref}</span></p>}
            </div>
          )}

          {/* ── success ── */}
          {state === 'success' && (
            <div className="text-center py-2">
              <div className="mx-auto rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                   style={{ width: 56, height: 56, background: 'rgba(25,135,84,0.15)' }}>
                <CheckCircle2 size={32} className="text-success" />
              </div>
              <h3 className="font-display fs-4 mb-1">
                {selectedVotes} {selectedVotes === 1 ? 'vote' : 'votes'} for {nominee?.name} confirmed!
              </h3>
              <p className="text-muted small">Thank you for supporting your favourite.</p>
              <button className="btn gold-chip w-100 fw-semibold mt-2" onClick={handleClose}>Done</button>
            </div>
          )}

          {/* ── error ── */}
          {state === 'error' && (
            <div className="text-center py-2">
              <div className="mx-auto rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                   style={{ width: 56, height: 56, background: 'rgba(220,53,69,0.15)' }}>
                <XCircle size={32} className="text-danger" />
              </div>
              <h3 className="font-display fs-4 mb-1">
                {tab === 'paypal' && errorMsg?.includes('initiated') ? 'Payment pending' : 'Payment not confirmed'}
              </h3>
              <p className="text-muted small">{errorMsg || 'Please try again.'}</p>
              <div className="d-flex gap-2 mt-2">
                <button className="btn btn-outline-secondary flex-grow-1" onClick={handleClose}>Close</button>
                {!(tab === 'paypal' && errorMsg?.includes('initiated')) && (
                  <button className="btn gold-chip flex-grow-1 fw-semibold" onClick={retry}>Try again</button>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, wide }) {
  return (
    <div className={wide ? 'col-6' : 'col-4'}>
      <div className="rounded-3 px-2 py-2 text-white" style={{ background: 'rgba(255,255,255,0.06)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }}>
        <div className="text-uppercase text-white-50" style={{ fontSize: 10, letterSpacing: 1.2 }}>{label}</div>
        <div className="font-mono fw-semibold small">{value}</div>
      </div>
    </div>
  );
}
